<template>
  <img
    :key="computedSrc"
    :alt="alt"
    :title="title"
    :src="!eager ? placeholderImageDataUri : computedSrc"
    :srcset="!eager ? undefined : srcset || undefined"
    :data-src="!eager ? computedSrc : null"
    :data-srcset="!eager ? srcset : null"
    :sizes="imageSizes || undefined"
    :loading="eager ? 'eager' : 'lazy'"
    :style="style"
    :width="width"
    :height="height"
    class="image"
    :class="{
      lazyload: !eager,
      'image--sprite': useImageSprite,
    }"
    :fetchpriority="priority ? 'high' : 'auto'"
    data-allow-mismatch
    @load="onImgLoaded"
    @error="onImgError"
  />
</template>

<script setup lang="ts">
import { camelCase } from 'change-case';
import { placeholderImageDataUri } from '@/utils/images';
import type { ImageSizes } from '@/composables/ImageSizer';

export type ImageOption = 'preload' | 'eager' | 'priority' | 'use_image_sprite' | 'rounded_corners' | 'rounded_display';
export type ImageFit = 'cover' | 'contain' | 'fill' | 'inside' | 'outside' | 'none';
export type ImageFormat = 'webp' | 'jpeg' | 'png';

type ImageOptionComputeds = Record<string, MaybeRef<boolean | undefined>>;

type ImageProps = {
  src: string;
  alt?: string;
  title?: string;
  fit?: ImageFit;
  width?: number | string;
  height?: number | string;
  minHeight?: number | string;
  transformation?: string;
  fetchPriority?: string;
  format?: ImageFormat;
  sizes?: ImageSizes | undefined;
  imageOptions?: ImageOption[];
};

const props = withDefaults(defineProps<ImageProps>(), {
  src: placeholderImageDataUri,
  alt: '',
  title: '',
  fit: 'contain',
  imageSprite: false,
  width: 0,
  height: 0,
  minHeight: 'auto',
  transformation: '',
  fetchPriority: 'auto',
  format: 'webp',
  sizes: undefined,
  imageOptions: undefined,
});

const emits = defineEmits(['loaded', 'error']);

const { preload, eager, priority, useImageSprite, roundedCorners, roundedDisplay } =
  (props.imageOptions?.reduce(
    (acc, curr) => {
      acc[camelCase(curr)] = computed(() => {
        return props.imageOptions?.includes(curr);
      });
      return acc;
    },
    {
      preload: false,
      eager: false,
      priority: false,
      useImageSprite: false,
      roundedCorners: false,
      roundedDisplay: false,
    } as ImageOptionComputeds,
  ) as ImageOptionComputeds) || {};

const computedSrc = computed(() => {
  // Nuxt optimizes some images into base64 data urls, they must not get params or suffixes.
  if (props.src?.startsWith('data:')) {
    return props.src;
  }
  if (props.transformation && !props.src.includes('?t=') && !useImageSprite) {
    return `${props.src}?f=${props.format}&t=${props.transformation}`;
  }
  return `${props.src}?f=${props.format}` + (useImageSprite ? '&t=cmssprite_m' : '');
});

const srcset = computed(() => {
  const srcset = [];

  if (!srcset.length) {
    if (useImageSprite) {
      srcset.push(`${props.src}?f=${props.format}&t=cmssprite_s 993w`);
      srcset.push(`${props.src}?f=${props.format}&t=cmssprite_m 1298w`);
      srcset.push(`${props.src}?f=${props.format}&t=cmssprite_l 1920w`);
    } else {
      return null;
    }
  }

  return srcset.join(',');
});

const sizes = computed(() => {
  if (useImageSprite && !props.sizes) {
    return '(max-width: 476px) 993px, (max-width: 768px) 1298px, 1920px';
  }

  return props.sizes || null;
});

const imageSizes = sizes.value ? useImageSizer(sizes.value) : undefined;

const style = computed(() => {
  const style: Record<string, string | number> = {};
  if (props.minHeight) {
    style.minHeight = typeof props.minHeight === 'number' ? `${props.minHeight}px` : props.minHeight;
  }

  if (roundedCorners) {
    style.borderRadius = '5px';
  }
  if (roundedDisplay) {
    style.borderRadius = '50%';
  }

  style.objectFit = props.fit;

  return style;
});

if (unref(preload) && unref(srcset) && unref(computedSrc)) {
  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        type: 'image/webp',
        imagesizes: imageSizes?.value || undefined,
        imagesrcset: srcset.value,
        href: computedSrc.value,
      },
    ],
  });
}

const onImgLoaded = () => {
  emits('loaded');
};

const onImgError = (event: Event) => {
  const imageNode = event.target as HTMLImageElement;
  const { classList } = imageNode;
  const processingClass = 'image--processing';

  if (!classList.contains(processingClass)) {
    classList.add(processingClass);
  }

  emits('error', event);
};
</script>

<style lang="scss" scoped>
.image {
  display: block;
  max-width: 100%;
  width: 100%;
  height: auto;
  min-height: 1px;

  &.image--processing,
  &.lazyload,
  &.lazyloading {
    background-color: palette(sand, light);
    background-image: url('#{$img-path}preloader.gif');
    background-repeat: no-repeat;
    background-position: 50%;
    background-size: 50px 50px;
  }

  &.image--processing {
    overflow: hidden;
    text-indent: 140%;
  }

  &.image--sprite {
    @include bp(m) {
      height: 386px;
      max-width: none;
      width: auto;
    }
  }
}
</style>
